import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
  onConfirm: () => void;
  onCancel?: () => void;
  loading?: boolean;
  error?: string | null;
  /**
   * Optional extra content rendered between the description and the action
   * buttons (e.g. a small period picker for bulk billing). Keeps the primitive
   * a confirm dialog while allowing a lightweight inline control.
   */
  children?: ReactNode;
  /**
   * Raises the dialog above another modal layer (BUG-506).
   *
   * The default stack (`z-50` overlay / `z-[60]` content) assumes this dialog is
   * the TOPMOST thing on screen. Opened from inside another Radix dialog — the
   * Orders filters panel is `z-[60]` overlay / `z-[70]` content — the defaults
   * put the confirm's own dimming UNDER that panel and its content level with
   * the panel's overlay, so it reads as "a dialog behind the modal", exactly as
   * the owner described it. Pass `elevated` when nesting.
   */
  elevated?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = "default",
  onConfirm,
  onCancel,
  loading,
  error,
  children,
  elevated = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t("common.confirm");
  const resolvedCancelLabel = cancelLabel ?? t("common.cancel");

  function handleCancel() {
    onCancel?.();
    onOpenChange(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      onCancel?.();
    }
    onOpenChange(next);
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 bg-black/40 backdrop-blur-sm",
            elevated ? "z-[90]" : "z-50",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl focus:outline-none",
            elevated ? "z-[100]" : "z-[60]",
          )}
        >
          <Dialog.Title className="mb-2 text-base font-bold text-gray-900">
            {title}
          </Dialog.Title>
          {description ? (
            <Dialog.Description className="mb-4 text-sm text-gray-500">
              {description}
            </Dialog.Description>
          ) : (
            // Visually-hidden fallback so Radix always finds an accessible
            // description and never emits the "Missing Description" warning,
            // even for callers that do not pass one.
            <Dialog.Description className="sr-only">{title}</Dialog.Description>
          )}
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {children && <div className="mb-4">{children}</div>}
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCancel}
              disabled={loading}
            >
              {resolvedCancelLabel}
            </Button>
            <Button
              variant={variant === "danger" ? "destructive" : "primary"}
              size="sm"
              onClick={onConfirm}
              disabled={loading}
            >
              {resolvedConfirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
